import { useEffect, useState, useRef } from "react";
import { api, errText, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Upload } from "lucide-react";

const FilePick = ({ label, file, setFile, inputRef, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.14em] text-gray-500">{label}</Label>
    <input
      ref={inputRef}
      data-testid={testId}
      type="file"
      accept="image/*,.heic,.heif"
      onChange={(e) => setFile(e.target.files?.[0] || null)}
      className="block w-full text-sm text-gray-600 file:mr-3 file:h-9 file:px-4 file:border file:border-gray-300 file:bg-white file:text-xs file:uppercase file:tracking-wider file:cursor-pointer border border-input h-11 pt-1 px-2 rounded-sm"
    />
    {file && <p className="text-xs text-gray-400 truncate">{file.name}</p>}
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.14em] text-gray-500">{label}</Label>
    {children}
  </div>
);

export const GalleryManager = () => {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [furnitureType, setFurnitureType] = useState("");
  const [caption, setCaption] = useState("");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const beforeRef = useRef();
  const afterRef = useRef();

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const add = async () => {
    if (!title.trim() || !beforeFile || !afterFile) {
      toast.error("Title, before photo and after photo are all required");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("furniture_type", furnitureType.trim());
    fd.append("caption", caption.trim());
    fd.append("before", beforeFile);
    fd.append("after", afterFile);
    try {
      const { data } = await api.post("/gallery", fd);
      setItems((it) => [data, ...it]);
      setTitle("");
      setFurnitureType("");
      setCaption("");
      setBeforeFile(null);
      setAfterFile(null);
      beforeRef.current.value = "";
      afterRef.current.value = "";
      toast.success("Transformation added to gallery");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/gallery/${id}`);
    setItems((it) => it.filter((x) => x.id !== id));
    toast.success("Removed");
  };

  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-5 items-start mb-4">
        <Field label="Title">
          <Input data-testid="gallery-title-input" placeholder="Walnut Dining Table" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-sm h-11" />
        </Field>
        <Field label="Furniture type (optional)">
          <Input data-testid="gallery-type-input" placeholder="Dining Table" value={furnitureType} onChange={(e) => setFurnitureType(e.target.value)} className="rounded-sm h-11" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Short caption (optional)">
            <Input data-testid="gallery-caption-input" placeholder="Same walnut colour — years of scratches and dullness removed." value={caption} onChange={(e) => setCaption(e.target.value)} className="rounded-sm h-11" />
          </Field>
        </div>
        <FilePick label="Before Photo" file={beforeFile} setFile={setBeforeFile} inputRef={beforeRef} testId="gallery-before-input" />
        <FilePick label="After Photo" file={afterFile} setFile={setAfterFile} inputRef={afterRef} testId="gallery-after-input" />
      </div>
      <Button data-testid="gallery-add-btn" onClick={add} disabled={uploading} variant="outline" className="rounded-sm h-11 text-xs uppercase tracking-wider mb-3">
        <Upload size={14} className="mr-1.5" /> {uploading ? "Uploading..." : "Add Transformation"}
      </Button>
      <p className="text-xs text-gray-400 mb-8 leading-relaxed">
        Use real Revival examples only — pieces that keep approximately the same wood colour. Photos are resized automatically.
      </p>

      <div className="grid sm:grid-cols-2 gap-6">
        {items.map((g) => (
          <div key={g.id} className="border border-gray-200 p-4" data-testid={`gallery-item-${g.id}`}>
            <div className="grid grid-cols-2 gap-2">
              {[["Before", g.before_photo_id], ["After", g.after_photo_id]].map(([lab, pid]) => (
                <div key={lab} className="relative">
                  <img src={`${API}/photos/${pid}`} alt={`${g.title} ${lab}`} className="w-full aspect-[4/3] object-cover" />
                  <span className="absolute bottom-1.5 left-1.5 bg-black/70 text-white text-[9px] uppercase tracking-[0.15em] px-2 py-0.5">{lab}</span>
                </div>
              ))}
            </div>
            <div className="flex items-start justify-between gap-3 mt-3">
              <div className="min-w-0">
                {g.furniture_type && <p className="text-[10px] uppercase tracking-[0.2em] text-[#c19a6b] font-bold">{g.furniture_type}</p>}
                <p className="text-sm font-medium">{g.title}</p>
                {g.caption && <p className="text-xs text-gray-500 mt-0.5">{g.caption}</p>}
              </div>
              <button data-testid={`gallery-delete-${g.id}`} onClick={() => remove(g.id)} className="text-gray-400 hover:text-red-600 transition-colors duration-150 shrink-0">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-gray-400 col-span-2">No transformations yet. These appear on the landing page and every customer quote to build trust.</p>}
      </div>
    </div>
  );
};
